import { useEffect, useRef, useState } from 'react';

type InfinitePagesOptions = {
    /**
     * What makes this a different list — filters, sort, which endpoint. A change
     * starts the list over, so it has to be a value, not an object identity:
     * build it with JSON.stringify over sorted keys.
     */
    queryKey: string;
    /** records of the page currently in the store */
    records: any[];
    loading: boolean;
    /** how many records the server says there are in total */
    total?: number;
    page: number;
    setPage: (page: number) => void;
    /** how far below the fold the next page starts loading */
    prefetchMargin?: string;
    getId?: (record: any) => any;
    /**
     * A row an incoming link named — see useHashAccordion. A list that pages on
     * scroll only holds the first page when it opens, so a link to the fortieth
     * service would open the top of the list and nothing else. While the row is
     * missing, pages keep coming without waiting for the reader.
     */
    seekKey?: string | null;
    /** is this the row that was asked for */
    seekMatch?: (record: any) => boolean;
    /** it turned up, or it is not coming — either way, stop looking */
    onSeekSettled?: () => void;
};

/**
 * How far the search above goes. A link to something that has since been
 * deleted matches nothing, and without a stop it would walk the entire list
 * a page at a time looking for it.
 */
const SEEK_PAGE_LIMIT = 10;

/**
 * Turns a paged resource into an endless one.
 *
 * The API still returns pages; they are stitched together here and the next one
 * is fetched as `sentinelRef` comes into view, so there is nothing to click.
 * Give the sentinel to `InfiniteFoot`, or place it yourself at the end of the
 * list.
 *
 * Works with any source that reports (records, loading, total) for a page and
 * takes a setPage — react-admin's useListContext, or a hand-rolled query.
 */
const useInfinitePages = ({
    queryKey,
    records,
    loading,
    total = 0,
    page,
    setPage,
    prefetchMargin = '360px',
    getId = (record: any) => record?.id,
    seekKey = null,
    seekMatch,
    onSeekSettled,
}: InfinitePagesOptions) => {
    // No key matches on mount on purpose: react-admin remembers the page per
    // resource, so the first response can be page 5 of an earlier visit and must
    // not be taken as the top of the list.
    const [loaded, setLoaded] = useState<{ key: string; items: any[]; stalled: boolean }>(
        { key: '', items: [], stalled: false },
    );
    const fresh = loaded.key === queryKey;
    const items = fresh ? loaded.items : [];

    // Back to the first page whenever the list starts over — including the
    // remembered page above, which an endless list has no way to scroll back
    // from. react-admin already does this for filter and sort changes, so this
    // is usually a no-op.
    const seenKey = useRef<string | null>(null);
    useEffect(() => {
        if (seenKey.current === queryKey) return;
        seenKey.current = queryKey;
        if (page !== 1) setPage(1);
    }, [queryKey]);

    const pageKey = `${queryKey}|${page}|${records.map(getId).join(',')}`;
    /* `records` is in the deps beside the key because the key cannot see a row
       whose contents changed under the same id — a role edited in place, then
       refreshed. Ids alone said "same page" and the list went on showing what
       the record used to say. Depending on the array is only safe because the
       updater below returns `prev` unchanged when nothing moved: a caller that
       builds a new array every render (`data || []`) would otherwise set state
       on every render and never stop. */
    useEffect(() => {
        if (loading) return;
        setLoaded((prev) => {
            // a fresh query only starts accumulating once its first page lands,
            // otherwise a page left over from the previous one opens a gap
            if (prev.key !== queryKey && page !== 1) return prev;
            // Page one IS the list, so it replaces rather than joins. react-admin
            // hands back the previous query's records — with loading already
            // false — while the new one is in flight, and merging those in is how
            // a filter change ends up showing what it filtered out.
            const base = (prev.key === queryKey && page > 1) ? prev.items : [];
            const byId = new Map<any, any>(base.map((record) => [getId(record), record]));
            records.forEach((record) => { if (record) byId.set(getId(record), record); });
            const next = Array.from(byId.values());
            // A page that brought nothing new means there is nothing left to
            // fetch, whatever the reported total says — without this, a total
            // that disagrees with the pages would scroll on forever.
            const stalled = page > 1 && next.length === base.length;

            // Same key, same rows, same records: hand back the state that is
            // already there, so React has nothing to re-render.
            const settled = prev.key === queryKey
                && prev.stalled === stalled
                && prev.items.length === next.length
                && prev.items.every((item, index) => item === next[index]);

            return settled ? prev : { key: queryKey, items: next, stalled };
        });
    }, [pageKey, loading, records]);

    const hasMore = items.length > 0 && items.length < total && !(fresh && loaded.stalled);
    const sentinelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const node = sentinelRef.current;
        // not observing while a page is in flight is what keeps one page from
        // being requested twice
        if (!node || !hasMore || loading) return undefined;
        const observer = new IntersectionObserver(
            (entries) => { if (entries.some((entry) => entry.isIntersecting)) setPage(page + 1); },
            { rootMargin: prefetchMargin },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, loading, page]);

    /* ---- the row a link asked for ---- */

    // held rather than depended on: both are written fresh on every render of
    // the caller, and neither says anything about whether to look again
    const seek = useRef({ match: seekMatch, settled: onSeekSettled });
    seek.current = { match: seekMatch, settled: onSeekSettled };

    const chased = useRef({ pages: 0, at: -1 });
    useEffect(() => { chased.current = { pages: 0, at: -1 }; }, [queryKey, seekKey]);

    useEffect(() => {
        // `fresh` first: an empty list under a key that has not answered yet is
        // "not loaded", not "not here", and chasing it would skip its own page 1
        if (!seekKey || !fresh || loading) return;
        const { match, settled } = seek.current;
        if (match && items.some(match)) {
            settled?.();
            return;
        }
        if (!hasMore || chased.current.pages >= SEEK_PAGE_LIMIT) {
            settled?.();
            return;
        }
        // One page in flight at a time. `loading` is not enough on its own:
        // setPage moves the page before the request it starts has been reported
        // as running, and this effect would walk several pages ahead of the one
        // it is still waiting for.
        if (chased.current.at === items.length) return;
        chased.current = { pages: chased.current.pages + 1, at: items.length };
        setPage(page + 1);
    }, [seekKey, fresh, loading, items, hasMore, page]);

    return {
        items,
        hasMore,
        sentinelRef,
        /**
         * The query changed and nothing has been gathered for it yet. Read it as
         * loading: an empty list here means "not yet", not "none".
         */
        settling: !fresh,
    };
};

export default useInfinitePages;
